import { useForm } from 'react-hook-form';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import { Button, Container, Dialog, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { getCurrentDateTime, getKeyPathValue, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

const ADMIT_PATIENT_HEADER = [
  {
    header: 'Dis Date',
    accessor: 'dis_date'
  },
  {
    header: 'Ipd No.',
    accessor: 'ipd_no'
  },
  {
    header: 'Patient Name',
    accessor: 'patient_name'
  },

  {
    header: 'Ward ',
    accessor: 'ward'
  },

  {
    header: 'Bill Date',
    accessor: 'bill_date'
  },
  {
    header: 'Due(Hr)',
    accessor: 'due'
  }
];

const TRANSFER_HISTORY_HEADER = [
  {
    header: 'Transfer Date ',
    accessor: 'transfer_date'
  },
  {
    header: 'Time',
    accessor: 'time'
  },
  {
    header: 'Old Ward',
    accessor: 'old_ward'
  },
  {
    header: 'Old Room',
    accessor: 'old_room'
  },
  {
    header: 'Old Bed No.',
    accessor: 'old_bed_no'
  },
  {
    header: 'New Ward',
    accessor: 'new_ward'
  },
  {
    header: 'New Room',
    accessor: 'new_room'
  },

  {
    header: 'New Bed No.',
    accessor: 'new_bed_no'
  },
  {
    header: 'Accept Date',
    accessor: 'accept_date'
  }
];

const initialOPDBillingDataObj = {
  data: [],
  loading: false
};

export const RelieveDischargePatient = () => {
  const [addDetailsDialogOpen, setAddDetailsDialogOpen] = useState(false);
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [opdBillingDataObj, setOpdBillingDataObj] = useState(initialOPDBillingDataObj);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [openPrint, setOpenPrint] = useState(false);
  const [OPDSearchDialogOpen, setOPDSearchDialogOpen] = useState(false);

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        uhid: Yup.string().required(' '),
        patient_name: Yup.string().required(' '),
        opd_type: Yup.string().required(' '),
        ref_type: Yup.string().required(' '),
        doctor: Yup.string().required(' '),
        sponsor: Yup.string().required(' '),
        department: Yup.string().required(' '),
        bill: Yup.object().shape({
          rec_amt_1: Yup.number().min(0, ' '),
          rec_amt_2: Yup.number().min(0, ' '),
          pay_mode: Yup.string().required(' ')
        })
      })
    ),
    defaultValues: {
      date: getCurrentDateTime('date'),
      time: getCurrentDateTime('time'),
      bill: {
        out_sourced: 'No',
        rec_amt_1: 0,
        rec_amt_2: 0,
        tds_amt: 0,
        disc_amt: 0,
        gst_amt: 0,
        net_amt: 0
      }
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  const formSubmit = async (formValues) => {
    setLoading(true);
    const payload = { uhid: formValues.uhid, ...formValues.bill, service_items: detailList };
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/' : '/', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: payload
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'edited' : 'created'} `, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
    } else {
      toast(`Failed to ${editFlag ? 'edit' : 'create'} `, { variant: 'error' });
    }
    setLoading(false);
  };

  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 2,
            p: 0.2,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: "100%",
            boxSizing: 'border-box',
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                pb: 0.5,
                mb: 3,
                borderBottom: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3">
                Relieve Discharged Patient
              </Typography>

              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <Button variant="contained">Apply</Button>
                <Button variant="contained">Exit</Button>
              </Stack>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={5} sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: '30rem' }}>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2 }}>Ward Name</Typography>
                  <SelectTag             formControlSX={{ flex: 0.8 }} name="ward_name" placeholder="Ward Name" />
                  <Button variant="contained" sx={{ flex: 0.1, minWidth: 0, p: 0.25, height: '100%' }}>
                    <SearchIcon />
                  </Button>
                </Stack>
                <Table
                  containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                  data={detailList}
                  columns={ADMIT_PATIENT_HEADER}
                  size="small"
                />
              </Grid>

              <Grid item xs={7} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2 }}>Relieve No.</Typography>
                  <TextField             sx={{ flex: 0.8 }} name="relieve_no" placeholder="Relieve No." disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2 }}> Relieve Date/Time</Typography>
                  <TextField             name="date" sx={{ flex: 0.4 }} disabled />
                  <TextField             name="time" sx={{ flex: 0.4 }} disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2 }}>Discharge No./Date</Typography>
                  <TextField             sx={{ flex: 0.4 }} name="discharge_no" placeholder="Discharge No" disabled />
                  <TextField             sx={{ flex: 0.4 }} name="date" placeholder="Discharge No" disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2 }}>IPD No.</Typography>
                  <TextField            
                    sx={{ flex: 0.8 }}
                    name="adm_no"
                    placeholder="Ipd No."
                    disabled={!['new', 'edit'].includes(currentAction)}
                  />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2 }}>Uhid No.</Typography>
                  <TextField             sx={{ flex: 0.8 }} name="uhid" placeholder="Uhid No." disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2 }}>Patient Name</Typography>
                  <TextField             sx={{ flex: 0.8 }} name="patient_name" placeholder="Patient Name" disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2 }}>Sponsor</Typography>
                  <TextField             sx={{ flex: 0.8 }} name="sponsor" placeholder="Sponsor" disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2 }}>Ward No.</Typography>
                  <TextField             sx={{ flex: 0.8 }} name="ward_no" placeholder="Ward No." disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2 }}>Bed/Room No.</Typography>
                  <TextField            sx={{ flex: 0.4 }} name="bed_no" placeholder="Bed No." disabled />
                  <TextField             sx={{ flex: 0.4 }} name="room_no" placeholder="Room No." disabled />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2 }}>Remarks</Typography>
                  <TextField             sx={{ flex: 0.8 }} name="remarks" placeholder="Remarks" />
                </Stack>
              </Grid>
            </Grid>
          </FormProvider>
        </Container>
      </Paper>

      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={1.5}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            OPD Billing Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Table
          sx={{ mt: 3 }}
          data={opdBillingDataObj.data || []}
          loading={opdBillingDataObj.loading}
          onRowClick={(row) => {
            delete row.patient;
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
      <Dialog
        open={addDetailsDialogOpen}
        onClose={() => setAddDetailsDialogOpen(false)}
        PaperProps={{ sx: { width: '80%', height: '90vh', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      ></Dialog>
      <Dialog
        open={openPrint}
        onClose={() => setOpenPrint(false)}
        PaperProps={{ sx: { width: '80%', height: '85%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      ></Dialog>
    </>
  );
};
